import { useState, useEffect, useCallback } from 'react';
import * as api from 'util/api';
import {
  readAvailableProductsCache,
  writeAvailableProductsCache,
  rememberWebstoreStore,
} from 'util/webstore';

/**
 * Shared catalog load/revalidate/refresh for Request Products, Order Merch, Add Onetimes.
 * Seeds from sessionStorage, revalidates in background, supports force refresh.
 */
const useAvailableProducts = (store) => {
  const cached = readAvailableProductsCache(store);
  const [products, setProducts] = useState(cached?.products ?? null);
  const [fetchedAt, setFetchedAt] = useState(cached?.fetchedAt ?? null);
  const [refreshing, setRefreshing] = useState(false);

  const applyCatalog = useCallback((data) => {
    if (!data?.products) {
      return;
    }
    writeAvailableProductsCache(store, data);
    setProducts(data.products);
    setFetchedAt(data.fetchedAt);
  }, [store]);

  useEffect(() => {
    const nextCached = readAvailableProductsCache(store);
    setProducts(nextCached?.products ?? null);
    setFetchedAt(nextCached?.fetchedAt ?? null);
    setRefreshing(false);

    let cancelled = false;
    api.getAvailableShopifyProducts(store)
      .then((data) => {
        if (cancelled || !data?.products) {
          return;
        }
        applyCatalog(data);
      });

    rememberWebstoreStore(store);

    return () => {
      cancelled = true;
    };
  }, [store, applyCatalog]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await api.getAvailableShopifyProducts(store, { refresh: true });
      if (data?.products) {
        applyCatalog(data);
      }
    } finally {
      setRefreshing(false);
    }
  }, [store, applyCatalog]);

  return {
    products,
    fetchedAt,
    loading: products === null,
    refreshing,
    refresh,
    setProducts,
  };
};

export default useAvailableProducts;
